// client/src/coms/soundboard/soundUpload.js
// Sound uploads: quick checks before sending (the server validates everything again with FFmpeg), the
// browser-decoded waveform for trimming, and multipart uploads with progress.
import { decodeAudioFile } from './audioPrep';

export const MAX_UPLOAD_BYTES = 1024 * 1024;
export const MAX_SOUND_SECONDS = 10;

// 'mp3' | 'wav' | '' from the first bytes (never from the file name).
export async function sniffAudioFile(file) {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (a, b) => String.fromCharCode(...head.slice(a, b));
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WAVE') return 'wav';
  if (ascii(0, 3) === 'ID3') return 'mp3';
  if (head[0] === 0xff && (head[1] & 0xe0) === 0xe0 && ((head[1] >> 1) & 0x03) === 0x01) return 'mp3';
  return '';
}

// -> { ok, error?, format, duration, decoded (for the waveform), needsTrim }
export async function inspectSoundFile(file) {
  if (!file) return { ok: false, error: 'No file selected.' };
  const format = await sniffAudioFile(file).catch(() => '');
  if (format !== 'mp3') return { ok: false, error: 'Only MP3 files can be uploaded here. Use the clip editor in Settings → Soundboard to convert other audio.' };
  if (file.size > MAX_UPLOAD_BYTES) return { ok: false, error: `“${file.name}” is ${(file.size / 1024 / 1024).toFixed(1)} MB; the limit is 1 MB.` };
  let decoded = null;
  try {
    decoded = await decodeAudioFile(file);
  } catch {
    return { ok: false, error: `“${file.name}” could not be decoded. It may be damaged.` };
  }
  return { ok: true, format, duration: decoded.duration, decoded, needsTrim: decoded.duration > MAX_SOUND_SECONDS + 0.05 };
}

// POST multipart with upload progress. -> { status, data }
export function uploadWithProgress(path, fields, file, onProgress) {
  return new Promise((resolve) => {
    const fd = new FormData();
    Object.entries(fields || {}).forEach(([k, v]) => {
      if (v != null && v !== '') fd.append(k, String(v));
    });
    if (file) fd.append('file', file, file.name || 'sound.mp3');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/coms${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === 'function') onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {}
      resolve({ status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ status: 0, data: { error: 'Network error. Check your connection and try again.' } });
    xhr.send(fd);
  });
}

export function nameFromFile(file) {
  return String((file && file.name) || 'Sound')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .slice(0, 40) || 'Sound';
}
